import { ArrowLeftIcon, ArrowRightIcon, SignInIcon } from '@primer/octicons-react';
import { Box, Checkbox, TextInput, TreeView } from '@primer/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { khoanNopApi } from '../../../api/khoanNopApi';
import SelectBoxHinhThucNopBom from '../../../components/selectbox-hinhthucnopbom/SelectBoxHinhThucNopBom';
import Button from '../../../components/ui/button';
import { NotifyHelper } from '../../../helpers/toast';
import { useCommonSelectedHook } from '../../../hooks/useCommonSelectedHook';
import { IKhoanNopChungTuVm, IKhoanNopTreeView, IKhoanNopVm } from '../../../models/response/finance/IKhoanNop';

interface DanhSachKhoanNopProps {
    hocSinh: any;
    khoanNopChungTuCreate: IKhoanNopChungTuVm[];
    hinhThucNopBomSelectedIds: number[];
    onSubmit: (data: IKhoanNopChungTuVm[], hinhThucNopBomIds: number[]) => void;
    onBack: () => void;
    onCancel: () => void;
    isLoading: boolean;

}

const DanhSachKhoanNop: React.FC<DanhSachKhoanNopProps> = ({ hocSinh, onSubmit, onBack, onCancel, khoanNopChungTuCreate, hinhThucNopBomSelectedIds, isLoading }) => {
    const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
    const [khoanNops, setKhoanNops] = useState<IKhoanNopVm[]>([]);
    const [khoanNopFilters, setKhoanNopFilters] = useState<IKhoanNopVm[]>([]);
    const [hinhThucNopBomIds, setHinhThucNopBomIds] = useState<number[]>(hinhThucNopBomSelectedIds ?? []);
    const [khoanNopChungTus, setKhoanNopChungTus] = useState<IKhoanNopChungTuVm[]>(khoanNopChungTuCreate ?? []);
    const [khoanNopChungTuTreeViews, setKhoanNopChungTuTreeViews] = useState<IKhoanNopTreeView[]>([]);
    const [expandedState, setExpandedState] = useState<Record<number, boolean>>({});

    useEffect(() => {
        getKhoanNopByHocSinhs();
    }, []);

    useEffect(() => {
        if (khoanNopFilters && khoanNopFilters.length > 0 && hinhThucNopBomIds.length > 0) {
            const filteredKhoanNops = khoanNops.filter(khoanNop => {
                const hinhThucNopBomIdsArray = khoanNop.sf_hinhthucnopbom_ids
                    .split(',')
                    .map(id => parseInt(id, 10));
                return hinhThucNopBomIdsArray.some(id => hinhThucNopBomIds.includes(id));
            });
            setKhoanNopFilters(filteredKhoanNops);
        } else {
            setKhoanNopFilters(khoanNops);
        }
    }, [hinhThucNopBomIds, khoanNops]);

    useEffect(() => {
        if (khoanNopFilters.length > 0) {
            const updatedKhoanNopChungTuTreeViews = khoanNopFilters.map(khoanNop => {
                const isChildChecked = khoanNopChungTus.some(chungTu =>
                    chungTu.sf_khoannop_id === khoanNop.id
                );
                const siblingKhoanNops = khoanNopFilters.filter(
                    (item) => item.sf_nhomkhoannop_id === khoanNop.sf_nhomkhoannop_id
                );
                const isParentChecked = siblingKhoanNops.every((sibling) =>
                    khoanNopChungTus.some(
                        (chungTu) =>
                            chungTu.sf_khoannop_id === sibling.id &&
                            chungTu.sf_nhomkhoannop_id === sibling.sf_nhomkhoannop_id
                    )
                );
                const so_tien_phai_nop = khoanNopChungTus.find(chungTu =>
                    chungTu.sf_khoannop_id === khoanNop.id
                )?.so_tien_phai_nop;

                return {
                    ...khoanNop,
                    child_checked: isChildChecked,
                    parent_checked: isParentChecked,
                    so_tien_phai_nop: so_tien_phai_nop ?? khoanNop.so_tien_dinh_muc
                };
            });
            setKhoanNopChungTuTreeViews(updatedKhoanNopChungTuTreeViews);
        }
    }, [khoanNopFilters, khoanNopChungTus]);


    const getKhoanNopByHocSinhs = async () => {
        const res = await khoanNopApi.selectAllByHocSinh(hocSinh.id, nam_hoc);
        if (res.is_success) {
            setKhoanNops(res.data);
            setKhoanNopFilters(res.data);
        } else {
            NotifyHelper.Error(res.message ?? 'Error');
        }
    };
    const handleCheckboxClick = (event: any) => {
        event.stopPropagation();
    };
    const handleParentCheckboxChange = (parent: any) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        setKhoanNopChungTus((prev) => updateKhoanNopChungTus(prev, parent, isChecked));
        parent.parent_checked = isChecked;
    };
    const updateKhoanNopChungTus = (prev: any[], parent: any, isChecked: boolean) => {
        if (isChecked) {
            const updatedEntries = khoanNopChungTuTreeViews.filter(
                (item) =>
                    item.sf_nhomkhoannop_id === parent.sf_nhomkhoannop_id
            ).map((subTree: any) => ({
                sf_nhomkhoannop_id: parent.sf_nhomkhoannop_id,
                sf_khoannop_id: subTree.id,
                so_tien_dinh_muc: subTree.so_tien_dinh_muc,
                so_tien_phai_nop: subTree.so_tien_phai_nop,
                sf_mucthuphi_id: subTree.sf_mucthuphi_id
            }));

            return [
                ...prev.filter(
                    (item) =>
                        item.sf_nhomkhoannop_id !== parent.sf_nhomkhoannop_id
                ),
                ...updatedEntries,
            ];
        } else {
            return prev.filter(
                (item) => item.sf_nhomkhoannop_id !== parent.sf_nhomkhoannop_id
            );
        }
    };
    const inputRefs = useRef(new Map());
    const handleTuitionFeeChange = useCallback(
        (subTree: IKhoanNopTreeView) => {
            let timeoutId: NodeJS.Timeout | null = null;

            return (event: React.ChangeEvent<HTMLInputElement>) => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                timeoutId = setTimeout(() => {
                    let value = event.target.value.replace(/[^\d]/g, '');
                    const numericValue = value ? parseInt(value, 10) : 0;

                    setKhoanNopChungTuTreeViews((prevTreeViews) =>
                        prevTreeViews.map((item) =>
                            item.id === subTree.id
                                ? { ...item, so_tien_phai_nop: numericValue }
                                : item
                        )
                    );
                    setKhoanNopChungTus((prevTreeViews) =>
                        prevTreeViews.map((item) =>
                            item.sf_khoannop_id === subTree.id
                                ? { ...item, so_tien_phai_nop: numericValue }
                                : item
                        )
                    );
                }, 500);
            };
        },
        []
    );
    const handleSubTaskCheckboxChange = (subTree: IKhoanNopTreeView) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        setKhoanNopChungTus((prev) => {
            const existingIndex = prev.findIndex(
                (chungTu) =>
                    chungTu.sf_khoannop_id === subTree.id &&
                    chungTu.sf_nhomkhoannop_id === subTree.sf_nhomkhoannop_id
            );
            if (checked) {
                if (existingIndex === -1) {
                    return [
                        ...prev,
                        {
                            sf_nhomkhoannop_id: subTree.sf_nhomkhoannop_id,
                            sf_khoannop_id: subTree.id,
                            so_tien_dinh_muc: subTree.so_tien_dinh_muc,
                            so_tien_phai_nop: subTree.so_tien_phai_nop ?? subTree.so_tien_dinh_muc,
                            sf_mucthuphi_id: subTree.sf_mucthuphi_id,
                        },
                    ];
                } else {
                    const updated = [...prev];
                    updated[existingIndex].so_tien_phai_nop = subTree.so_tien_phai_nop ?? subTree.so_tien_dinh_muc;
                    return updated;
                }
            } else {
                return prev.filter(
                    (chungTu) =>
                        chungTu.sf_khoannop_id !== subTree.id ||
                        chungTu.sf_nhomkhoannop_id !== subTree.sf_nhomkhoannop_id
                );
            }
        });
    };
    const handleExpandedChange = (id: number) => {
        setExpandedState((prev) => ({
            ...prev,
            [id]: !prev[id], // Đảo ngược trạng thái hiện tại
        }));
    };


    const parentTrees = useMemo(() => {
        if (khoanNopChungTuTreeViews.length === 0) return [];
        const result = Array.from(
            new Map(
                khoanNopChungTuTreeViews.map((item) => [
                    item.sf_nhomkhoannop_id,
                    {
                        nhom_khoan_nop: item.nhom_khoan_nop,
                        sf_nhomkhoannop_id: item.sf_nhomkhoannop_id,
                        parent_checked: item.parent_checked,
                    },

                ])
            ).values()
        );
        console.log(result);
        return result;
    }, [khoanNopChungTuTreeViews]);

    useEffect(() => {
        const initialExpandedState = parentTrees.reduce((acc, parent) => {
            acc[parent.sf_nhomkhoannop_id] = true;
            return acc;
        }, {} as Record<number, boolean>);
        setExpandedState(initialExpandedState);

    }, [parentTrees]);
    const groupedSubTrees = useMemo(() => {
        const map = new Map();
        khoanNopChungTuTreeViews.forEach((item) => {
            if (!map.has(item.sf_nhomkhoannop_id)) {
                map.set(item.sf_nhomkhoannop_id, []);
            }
            map.get(item.sf_nhomkhoannop_id).push(item);
        });
        return map;
    }, [khoanNopChungTuTreeViews]);
    const treeViews = useMemo(() => {
        if (parentTrees.length === 0) return null;
        return (
            <TreeView aria-label="Issues">
                {parentTrees.map((parent, index) => (
                    <TreeView.Item
                        expanded={expandedState[parent.sf_nhomkhoannop_id] || false}
                        id={`${index}`}
                        key={parent.sf_nhomkhoannop_id}
                        onExpandedChange={(expanded) => handleExpandedChange(parent.sf_nhomkhoannop_id)}
                    >
                        <TreeView.LeadingVisual>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Checkbox
                                    id={`${index}`}
                                    checked={parent.parent_checked}
                                    onChange={handleParentCheckboxChange(parent)}
                                    onClick={handleCheckboxClick}
                                    aria-label={`${parent.nhom_khoan_nop}`}
                                />
                            </Box>
                        </TreeView.LeadingVisual>
                        <Box sx={{
                            display: 'grid', gap: 2, gridTemplateColumns: '550px 1fr 1fr', borderBottom: '1px solid',
                            borderColor: 'border.default',
                        }}>
                            <span style={{ fontWeight: 600 }}>{parent.nhom_khoan_nop}</span>
                            <span style={{ fontWeight: 600 }}>Số tiền định mức</span>
                            <span style={{ fontWeight: 600 }}>Số tiền phải nộp</span>
                        </Box>
                        <TreeView.SubTree state="initial" >
                            {groupedSubTrees.get(parent.sf_nhomkhoannop_id).map((subTree: IKhoanNopTreeView, idxSubTree: number) => (
                                <Box key={`${index}-${idxSubTree}`} sx={{ marginBottom: '3px' }}>
                                    <TreeView.Item expanded={true} id={`${index}-${idxSubTree}`}>
                                        <TreeView.LeadingVisual>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '3px' }}>
                                                <Checkbox
                                                    id={`${idxSubTree}`}
                                                    checked={subTree.child_checked}
                                                    onChange={handleSubTaskCheckboxChange(subTree)}
                                                    aria-label={`${subTree.ten_khoan_nop}`}
                                                />
                                            </Box>
                                        </TreeView.LeadingVisual>
                                        <Box sx={{
                                            display: 'grid', gap: 2, gridTemplateColumns: '550px 1fr 1fr', borderBottom: '1px solid',
                                            borderColor: 'border.default',
                                        }}>
                                            <span style={{ marginTop: '5px' }}>{subTree.ten_khoan_nop}</span>
                                            <span style={{ marginTop: '5px' }}>{subTree.so_tien_dinh_muc.toLocaleString('de-DE')}</span>
                                            {/* <TextInput
                                                key={subTree.id}
                                                aria-label="Số tiền phải nộp"
                                                defaultValue={subTree.so_tien_phai_nop.toLocaleString('de-DE')}
                                                onBlur={handleTuitionFeeChange(subTree)}
                                                ref={(el) => inputRefs.current.set(subTree.id, el)}
                                                sx={{ width: 100 }}
                                                size="small"
                                            /> */}
                                            <TextInput
                                                key={subTree.id}
                                                aria-label="Số tiền phải nộp"
                                                defaultValue={subTree.so_tien_phai_nop.toLocaleString('de-DE')}
                                                onBlur={handleTuitionFeeChange(subTree)} // Khi mất focus
                                                ref={(el) => inputRefs.current.set(subTree.id, el)}
                                                sx={{ width: 100 }}
                                                size="small"
                                            />

                                        </Box>
                                    </TreeView.Item>
                                </Box>
                            ))}
                        </TreeView.SubTree>
                    </TreeView.Item>
                ))}
            </TreeView>
        );
    }, [parentTrees, groupedSubTrees, expandedState]);


    const handleSubmit = () => {
        onSubmit(khoanNopChungTus, hinhThucNopBomIds);
    }
    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'border.default',
                borderRadius: 2,
                // maxHeight: 'calc(100vh - 230px)',
                overflowY: 'auto',
                p: 1,
            }}
        >
            <Box
                sx={{
                    borderBottom: '1px solid',
                    borderColor: 'border.default',
                    pb: 1,
                    ml: 1
                }}
            >
                <SelectBoxHinhThucNopBom
                    value={hinhThucNopBomIds}
                    isShowClearBtn={true}
                    onValueChanged={setHinhThucNopBomIds}
                    width="100%"
                />
            </Box>
            <Box
                sx={{
                    borderBottom: '1px solid',
                    borderColor: 'border.default',
                    pb: 1,
                    pt: 2,
                    height: 'calc(90vh - 310px)',
                    overflowY: 'auto'
                }}
            >
                {treeViews}
            </Box>
            <Box sx={{ mt: 1, mr: 1, display: "flex", justifyContent: "space-between" }}>
                <Button sx={{ ml: 1 }}
                    text='Thoát'
                    size='medium'
                    leadingVisual={SignInIcon}
                    onClick={onCancel}
                />
                <Box
                    sx={{ mr: 1, display: 'flex', justifyContent: 'flex-end' }}
                >
                    <Button
                        text="Quay lại"
                        sx={{ mr: 2 }}
                        size="medium"
                        leadingVisual={ArrowLeftIcon}
                        onClick={onBack}
                    />
                    <Button
                        text="Tạo báo phí"
                        trailingVisual={ArrowRightIcon}
                        variant="primary"
                        disabled={hinhThucNopBomIds.length == 0}
                        size="medium"
                        isLoading={isLoading}
                        onClick={handleSubmit}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default DanhSachKhoanNop;
